import { Calendar as CalendarIcon, X } from 'lucide-react';
import { type ReactElement, useCallback, useState } from 'react';
import type { DateRange } from 'react-day-picker';

import { Label, Stack } from '@crossnokaye/ux-common';

import { Button } from '@/ux-common/components/button/button';
import { formatDate } from '@/ux-common/components/calendar';
import { Calendar } from '@/ux-common/components/calendar/calendar';
import { cn } from '@/ux-common/lib/utils.ts';

import type { DateRangeFilterProps, DateRangeValue } from '../types';

/**
 * Date range filter with Range/Single mode toggle and Calendar.
 * Matches the old data-table filter UI.
 */
export const DateRangeFilter = ({
  value,
  onChange,
  onClear,
  isActive,
  onClose,
}: DateRangeFilterProps & { isActive: boolean; onClose: () => void }): ReactElement => {
  const [mode, setMode] = useState<'range' | 'single'>(value.equals ? 'single' : 'range');

  const updateFilter = useCallback(
    (newFilter: Partial<DateRangeValue>) => {
      const updatedFilter = { ...value, ...newFilter };

      // Clean up undefined values
      Object.keys(updatedFilter).forEach((key) => {
        if (updatedFilter[key as keyof DateRangeValue] === undefined) {
          delete updatedFilter[key as keyof DateRangeValue];
        }
      });

      // If all fields are empty, clear the filter completely
      if (Object.keys(updatedFilter).length === 0) {
        onClear();
      } else {
        onChange(updatedFilter);
      }
    },
    [value, onChange, onClear]
  );

  const handleDateRangeSelect = useCallback(
    (range: DateRange | undefined) => {
      if (range) {
        updateFilter({
          from: range.from,
          to: range.to,
          equals: undefined, // Clear equals when setting range
        });
      }
    },
    [updateFilter]
  );

  const handleSingleDateSelect = useCallback(
    (date: Date | undefined) => {
      updateFilter({
        equals: date,
        from: undefined, // Clear range when setting equals
        to: undefined,
      });
    },
    [updateFilter]
  );

  return (
    <>
      <Stack gap="default" className="items-center w-min">
        <div className="p-3 border-b border-border w-full">
          <div className="flex items-center justify-between mb-3">
            <div className="flex gap-2">
              <Button
                className={cn(
                  `btn btn-default btn-sm font-semibold`,
                  mode === 'range' ? 'btn-brand' : 'btn-tier2'
                )}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setMode('range');
                }}
              >
                Range
              </Button>
              <Button
                className={cn(
                  'btn btn-default btn-sm font-semibold',
                  mode === 'single' ? 'btn-brand' : 'btn-tier2'
                )}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setMode('single');
                }}
              >
                Single Date
              </Button>
            </div>
          </div>

          {mode === 'range' && (
            <div className="flex gap-2 text-sm">
              <div className="flex-1">
                <Label className="text-xs text-foreground/70">From</Label>
                <div className="flex items-center gap-2 p-2 border rounded mt-1">
                  <CalendarIcon size={14} className="text-foreground/50" />
                  <span className="text-sm font-semibold normal-case">
                    {(value.from && formatDate(value.from, { format: 'medium' })) || 'Start'}
                  </span>
                  {value.from && (
                    <X
                      size={14}
                      className="cursor-pointer text-foreground/50 hover:text-foreground ml-auto"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        updateFilter({ from: undefined });
                      }}
                    />
                  )}
                </div>
              </div>
              <div className="flex-1">
                <Label className="text-xs text-foreground/70">To</Label>
                <div className="flex items-center gap-2 p-2 border rounded mt-1">
                  <CalendarIcon size={14} className="text-foreground/50" />
                  <span className="text-sm font-semibold normal-case">
                    {(value.to && formatDate(value.to, { format: 'medium' })) || 'End'}
                  </span>
                  {value.to && (
                    <X
                      size={14}
                      className="cursor-pointer text-foreground/50 hover:text-foreground ml-auto"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        updateFilter({ to: undefined });
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {mode === 'single' && (
            <div>
              <Label className="text-xs text-foreground/70">Selected Date</Label>
              <div className="flex items-center gap-2 p-2 border rounded mt-1">
                <CalendarIcon size={14} className="text-foreground/50" />
                <span className="text-sm font-semibold normal-case">
                  {(value.equals && formatDate(value.equals, { format: 'medium' })) ||
                    'Select date'}
                </span>
                {value.equals && (
                  <X
                    size={14}
                    className="cursor-pointer text-foreground/50 hover:text-foreground ml-auto"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      updateFilter({ equals: undefined });
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {mode === 'range' ? (
          <div className="date-range-calendar">
            <Calendar
              mode="range"
              selected={{
                from: value.from,
                to: value.to,
              }}
              onSelect={handleDateRangeSelect}
            />
            <style
              dangerouslySetInnerHTML={{
                __html: `
                .date-range-calendar .rdp-range_start:not(.range-end) {
                  border-radius: 0.375rem 0 0 0.375rem !important;
                }
                .date-range-calendar .range-end:not(.rdp-range_start) {
                  border-radius: 0 0.375rem 0.375rem 0 !important;
                }
                .date-range-calendar .rdp-range_start.range-end {
                  border-radius: 0.375rem !important;
                }
                .date-range-calendar [aria-selected="true"]:not(.rdp-range_start):not(.range-end) {
                  border-radius: 0 !important;
                }
              `,
              }}
            />
          </div>
        ) : (
          <Calendar mode="single" selected={value.equals} onSelect={handleSingleDateSelect} />
        )}
      </Stack>

      {/* Footer with Clear/Close buttons */}
      <div className="flex justify-end p-3 border-t border-border">
        {isActive && (
          <Button
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onClear();
            }}
            className="btn btn-tier3 btn-sm mr-2 font-semibold"
          >
            Clear
          </Button>
        )}
        <Button onClick={onClose} className="btn btn-tier2 btn-sm font-semibold">
          Close
        </Button>
      </div>
    </>
  );
};

DateRangeFilter.displayName = 'DateRangeFilter';
